import { Controller } from "@hotwired/stimulus"
import { SOURCE_LIMIT, canvasFor, sanitizeSvg, readRaster, fitLogo } from "../lib/admin_charity_logo_image"

export default class extends Controller {
  static targets = ["dialog", "file", "drop", "preview", "filename", "controls", "pageControl", "page", "trim", "background", "padding", "status", "error", "save", "closeButton", "currentImage", "currentCaption", "initials", "saved"]
  static values = {url: String, version: String, pdfModule: String, pdfWorker: String}
  connect() {
    this.active = true; this.generation = 0
    this.beforeCache = () => this.dialogTarget.close()
    document.addEventListener("turbo:before-cache", this.beforeCache)
  }
  disconnect() {
    this.active = false; this.generation++
    this.pdfTask?.destroy().catch(() => {})
    document.removeEventListener("turbo:before-cache", this.beforeCache)
    this.dialogTarget.close()
  }
  open(event) {
    this.opener = event.currentTarget
    this.reset()
    this.dialogTarget.showModal()
    this.fileTarget.focus()
  }
  reset() {
    this.generation++; this.source = null; this.processing = false
    this.pdfTask?.destroy().catch(() => {}); this.pdfTask = null; this.pdf = null
    this.fileTarget.value = ""; this.previewTarget.hidden = true
    this.controlsTarget.hidden = true; this.pageControlTarget.hidden = true
    this.trimTarget.checked = true; this.backgroundTarget.value = "transparent"; this.paddingTarget.value = "5"
    this.filenameTarget.textContent = "Choose a file to preview"
    this.statusTarget.textContent = ""; this.showError(""); this.saveTarget.disabled = true
    this.dropTarget.classList.remove("dragging")
  }
  close() { if (!this.saving) this.dialogTarget.close() }
  cancel(event) { if (this.saving) event.preventDefault() }
  closed() { this.reset(); this.opener?.focus() }
  backdrop(event) {
    if (event.target !== this.dialogTarget) return
    const box = this.dialogTarget.getBoundingClientRect()
    if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.close()
  }
  dragover(event) { event.preventDefault(); if (!this.saving) this.dropTarget.classList.add("dragging") }
  dragleave() { this.dropTarget.classList.remove("dragging") }
  drop(event) {
    event.preventDefault(); this.dragleave()
    if (this.saving) return
    if (event.dataTransfer.files.length !== 1) { this.showError("Choose one logo file at a time."); return }
    this.load(event.dataTransfer.files[0])
  }
  choose() { if (!this.saving && this.fileTarget.files[0]) this.load(this.fileTarget.files[0]) }
  async load(file) {
    this.reset()
    const generation = this.generation
    this.processing = true; this.statusTarget.textContent = "Preparing your preview…"
    this.filenameTarget.textContent = `${file.name} · ${Math.ceil(file.size / 1024)} KB`
    try {
      if (!file.size || file.size > SOURCE_LIMIT) throw new Error("Choose a file smaller than 20 MB.")
      const extension = file.name.split(".").pop().toLowerCase()
      if (!["png", "jpg", "jpeg", "webp", "svg", "pdf", "ai"].includes(extension)) throw new Error("Choose PNG, JPG, WebP, SVG, PDF or PDF-compatible AI.")
      this.vector = ["svg", "pdf", "ai"].includes(extension)
      if (["pdf", "ai"].includes(extension)) {
        const bytes = new Uint8Array(await file.arrayBuffer())
        if (!new TextDecoder().decode(bytes.slice(0, 1024)).includes("%PDF-")) throw new Error("This Illustrator file has no PDF preview. Export it as PDF, SVG or PNG, then choose that file.")
        const pdfjs = await import(this.pdfModuleValue)
        if (!this.current(generation)) return
        pdfjs.GlobalWorkerOptions.workerSrc = this.pdfWorkerValue
        const task = pdfjs.getDocument({data: bytes, isEvalSupported: false, useSystemFonts: true, useWasm: false})
        this.pdfTask = task
        const pdf = await task.promise
        if (!this.current(generation)) { task.destroy(); return }
        if (pdf.numPages > 100) throw new Error("Choose a PDF with 100 pages or fewer, or export just the logo page.")
        this.pdf = pdf
        this.pageTarget.replaceChildren(...Array.from({length: pdf.numPages}, (_, i) => new Option(`Page ${i + 1}`, String(i + 1))))
        this.pageControlTarget.hidden = pdf.numPages === 1
        await this.renderPage(generation)
      } else {
        const input = extension === "svg" ? sanitizeSvg(await file.text()) : file
        const source = await readRaster(input)
        if (!this.current(generation)) return
        this.source = source
      }
      if (!this.current(generation)) return
      this.controlsTarget.hidden = false; this.processing = false; this.redraw()
    } catch (error) {
      if (!this.current(generation)) return
      this.processing = false; this.source = null; this.saveTarget.disabled = true
      this.statusTarget.textContent = ""
      this.showError(error.name === "PasswordException" ? "This PDF is password-protected. Export an unlocked copy of the logo." : (error.message || "This file could not be previewed. Try PNG or PDF."))
    }
  }
  current(generation) { return this.active && this.dialogTarget.open && generation === this.generation }
  async renderPage(generation) {
    const page = await this.pdf.getPage(Number(this.pageTarget.value))
    const original = page.getViewport({scale: 1})
    const viewport = page.getViewport({scale: 1600 / Math.max(original.width, original.height)})
    const canvas = canvasFor(Math.max(1, Math.ceil(viewport.width)), Math.max(1, Math.ceil(viewport.height)))
    await page.render({canvasContext: canvas.getContext("2d"), viewport, background: "rgba(0,0,0,0)"}).promise
    if (this.current(generation)) this.source = canvas
    page.cleanup()
  }
  async changePage() {
    if (this.saving || !this.pdf) return
    const generation = ++this.generation
    this.processing = true; this.saveTarget.disabled = true; this.showError("")
    this.statusTarget.textContent = "Preparing page…"
    try {
      await this.renderPage(generation)
      if (!this.current(generation)) return
      this.processing = false; this.redraw()
    } catch (_) {
      if (!this.current(generation)) return
      this.source = null; this.processing = false; this.statusTarget.textContent = ""
      this.showError("This PDF page could not be rendered. Choose another page or file.")
    }
  }
  redraw() {
    if (!this.source || this.processing || this.saving) return
    const bounds = fitLogo(this.source, this.previewTarget, {trim: this.trimTarget.checked, padding: Number(this.paddingTarget.value), background: this.backgroundTarget.value})
    this.previewTarget.hidden = false; this.saveTarget.disabled = false
    this.statusTarget.textContent = !this.vector && Math.max(bounds.width, bounds.height) < 400 ? "Small source image: the saved logo may look soft. A larger original is recommended." : "Ready · 800 × 800 PNG. Check the preview, then save."
  }
  showError(message) { this.errorTarget.textContent = message; this.errorTarget.hidden = !message }
  setSaving(value) {
    this.saving = value
    this.dialogTarget.querySelectorAll("input, select, button").forEach(el => { el.disabled = value })
    this.saveTarget.disabled = value || !this.source || this.processing
    this.saveTarget.textContent = value ? "Saving…" : "Save logo"
  }
  async save() {
    if (this.saving || this.processing || !this.source) return
    this.setSaving(true); this.showError("")
    try {
      const blob = await new Promise(resolve => this.previewTarget.toBlob(resolve, "image/png"))
      if (!blob || blob.size > 4 * 1024 * 1024) throw new Error("The prepared logo is too large. Try a simpler source image.")
      const data = new FormData()
      data.append("logo", blob, "logo.png"); data.append("logo_version", this.versionValue)
      const response = await fetch(this.urlValue, {method: "PATCH", body: data, headers: {Accept: "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""}})
      if (response.redirected) throw new Error("Your admin session expired. Reload the page and sign in again.")
      const result = await response.json()
      if (!response.ok) throw new Error(result.error || "The logo could not be saved. Please retry.")
      if (!this.active) return
      this.currentImageTargets.forEach(img => { img.src = result.url; img.hidden = false })
      this.initialsTargets.forEach(el => { el.hidden = true })
      this.currentCaptionTarget.textContent = "Current charity logo"
      this.versionValue = result.version; this.savedTarget.textContent = "Logo saved."
      this.dialogTarget.close()
    } catch (error) {
      if (this.active) this.showError(error instanceof SyntaxError || error instanceof TypeError ? "The save could not be confirmed. Reload the page to check its current logo before retrying." : error.message)
    } finally { if (this.active) this.setSaving(false) }
  }
}
